import { useMutation, useQueryClient } from 'react-query'
import { ScheduledPostTargetsClient } from 'api/clients/ScheduledPostTargetsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { scheduledPostTargetsKeys } from './scheduledPostTargetsKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ScheduledPostTarget } from 'types'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'

export const usePublishScheduledPostTargets = ({
  onSuccess,
  onFailure,
  onSettled,
}: {
  onSuccess?: () => void
  onFailure?: () => void
  onSettled?: () => void
} = {}) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const mutation = useMutation(
    async ({ scheduledPostId, targetIds }: { scheduledPostId: number; targetIds?: number[] }) => {
      const res = await ScheduledPostTargetsClient.publish({
        workspaceId,
        scheduledPostId,
        targetIds,
      })
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Error publishing scheduled post targets')
      }
      return res.data
    },
    {
      onMutate: async ({ scheduledPostId, targetIds }) => {
        // Optimistically update the cache to show targets as processing
        const queryKey = scheduledPostTargetsKeys.list(workspaceId, scheduledPostId)
        const previousData = queryClient.getQueryData<ScheduledPostTarget[]>(queryKey)

        if (previousData) {
          // Update only specific targets if targetIds are provided, otherwise update all
          const optimisticData = previousData.map((target) => {
            if (targetIds && targetIds.length > 0) {
              // Only update targets that are in the targetIds array
              return targetIds.includes(target.id) ? { ...target, status: 'processing' as const } : target
            } else {
              // Update all targets if no specific targetIds provided
              return { ...target, status: 'processing' as const }
            }
          })

          queryClient.setQueryData(queryKey, optimisticData)
        }

        return { previousData, queryKey }
      },
      onError: (error: Error, variables, context) => {
        // Revert optimistic update on error
        if (context?.previousData && context?.queryKey) {
          queryClient.setQueryData(context.queryKey, context.previousData)
        }
        // Invalidate on error to ensure we have fresh data
        queryClient.invalidateQueries(scheduledPostTargetsKeys.list(workspaceId, variables.scheduledPostId))
        showToastMessage(error.message, 'error')
        onFailure?.()
      },
      onSuccess: () => {
        onSuccess?.()
      },
      onSettled: (_data, error, variables, _context) => {
        // Invalidate calendar queries on success so PostSettings gets fresh data
        // This ensures banners disappear when target status changes
        if (!error) {
          queryClient.invalidateQueries(calendarKeys.all)
          queryClient.invalidateQueries(scheduledPostTargetsKeys.list(workspaceId, variables.scheduledPostId))
          onSettled?.()
        }
      },
    },
  )

  return mutation
}
